import { useState } from 'react';
import type { Reservation } from '../../../mocks/reservations';

interface Props {
  reservations: Reservation[];
  onStatusChange: (id: string, status: Reservation['status']) => void;
}

const statusConfig = {
  beklemede: { label: 'Beklemede', class: 'bg-amber-50 text-amber-600 border-amber-200' },
  onaylandi: { label: 'Onaylandı', class: 'bg-green-50 text-green-600 border-green-200' },
  iptal: { label: 'İptal', class: 'bg-red-50 text-red-500 border-red-200' },
};

export default function ReservationTable({ reservations, onStatusChange }: Props) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('hepsi');
  const [selected, setSelected] = useState<Reservation | null>(null);
  const [sortBy, setSortBy] = useState<'createdAt' | 'departureDate'>('createdAt');

  const filtered = reservations
    .filter((r) => {
      const q = search.toLowerCase();
      const matchSearch =
        r.firstName.toLowerCase().includes(q) ||
        r.lastName.toLowerCase().includes(q) ||
        r.tourName.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.phone.includes(q);
      const matchStatus = statusFilter === 'hepsi' || r.status === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'createdAt') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return new Date(a.departureDate).getTime() - new Date(b.departureDate).getTime();
    });

  const formatDate = (iso: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm" />
          </div>
          <input
            type="text"
            placeholder="İsim, tur, e-posta veya telefon ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['hepsi', 'beklemede', 'onaylandi', 'iptal'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-2 rounded-xl text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                statusFilter === s
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
              }`}
            >
              {s === 'hepsi' ? 'Tümü' : s === 'beklemede' ? 'Beklemede' : s === 'onaylandi' ? 'Onaylandı' : 'İptal'}
            </button>
          ))}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'createdAt' | 'departureDate')}
            className="border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-600 focus:outline-none focus:border-orange-400 cursor-pointer"
          >
            <option value="createdAt">Oluşturma Tarihi</option>
            <option value="departureDate">Hareket Tarihi</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">MÜŞTERİ</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">TUR</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">HAREKET TARİHİ</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">KİŞİ</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">TOPLAM</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">DURUM</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">İŞLEM</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-sm text-gray-400 py-12">
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                      <i className="ri-inbox-line text-gray-400 text-lg" />
                    </div>
                    Sonuç bulunamadı
                  </td>
                </tr>
              )}
              {filtered.map((r, i) => (
                <tr
                  key={r.id}
                  className={`border-b border-gray-50 hover:bg-orange-50/30 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/20'}`}
                >
                  <td className="px-5 py-4">
                    <p className="text-sm font-semibold text-gray-900">{r.firstName} {r.lastName}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{r.email}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-gray-700 max-w-[220px] truncate">{r.tourName}</p>
                    <p className="text-xs text-gray-400 mt-0.5">#{r.id}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-gray-700">{r.departureDate ? formatDate(r.departureDate) : '-'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Talep: {formatDate(r.createdAt)}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-gray-700">{r.adults + r.children} kişi</p>
                    <p className="text-xs text-gray-400">{r.adults} yetişkin{r.children > 0 ? `, ${r.children} çocuk` : ''}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm font-semibold text-gray-900">
                      {r.currency === 'TL' ? `₺${r.totalPrice.toLocaleString('tr-TR')}` : `€${r.totalPrice.toLocaleString('tr-TR')}`}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${statusConfig[r.status].class}`}>
                      {statusConfig[r.status].label}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelected(r)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer"
                        title="Detay Gör"
                      >
                        <i className="ri-eye-line text-sm" />
                      </button>
                      <select
                        value={r.status}
                        onChange={(e) => onStatusChange(r.id, e.target.value as Reservation['status'])}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:border-orange-400 cursor-pointer"
                      >
                        <option value="beklemede">Beklemede</option>
                        <option value="onaylandi">Onayla</option>
                        <option value="iptal">İptal Et</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/40 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            <span className="font-semibold text-gray-700">{filtered.length}</span> rezervasyon gösteriliyor
          </p>
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative bg-white rounded-2xl border border-gray-100 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-base font-bold text-gray-900">{selected.firstName} {selected.lastName}</h3>
                <p className="text-xs text-gray-400 mt-0.5">Rezervasyon #{selected.id}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 transition-colors cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-orange-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-orange-600 mb-1">TUR BİLGİSİ</p>
                <p className="text-sm font-medium text-gray-800">{selected.tourName}</p>
                <div className="flex gap-4 mt-2 text-xs text-gray-500">
                  <span><i className="ri-calendar-line mr-1" />{selected.departureDate ? formatDate(selected.departureDate) : '-'}</span>
                  <span><i className="ri-group-line mr-1" />{selected.adults} yetişkin{selected.children > 0 ? `, ${selected.children} çocuk` : ''}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">E-posta</p>
                  <p className="text-sm text-gray-800">{selected.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Telefon</p>
                  <p className="text-sm text-gray-800">{selected.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Toplam Tutar</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {selected.currency === 'TL' ? `₺${selected.totalPrice.toLocaleString('tr-TR')}` : `€${selected.totalPrice.toLocaleString('tr-TR')}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Talep Tarihi</p>
                  <p className="text-sm text-gray-800">{formatDate(selected.createdAt)}</p>
                </div>
              </div>

              {selected.specialRequests && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">Özel İstekler</p>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selected.specialRequests}</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <a
                  href={`mailto:${selected.email}`}
                  className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-xl py-2.5 text-sm text-gray-600 hover:border-orange-400 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-line" />
                  E-posta Gönder
                </a>
                <a
                  href={`tel:${selected.phone}`}
                  className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line" />
                  Ara
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
